// EXTERNAL
import { useState } from "react";
import { Link } from "react-router-dom";
// ASSETS
import betaLogo from "../../../../assets/beta-logo.png";
// CONFIG
import config from "../../../../configs/config";
// COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
// FUNCTIONS
import { fetchAdsLibraryPageData } from "../functions/fetch";
import { fetchMediaAssetWithProxy, uploadMediaAssetsWithProxy } from "../functions/proxy";
import { mostApplicableFormatAndAspectRatio } from "../functions/mostApplicableFormatAndAspectRatio";
// LIBS
import { analytics_record } from "../../../../libs/amplify";
import { Google_Analytics_Event } from "../../../../libs/google-analytics";
import { logger } from "../../../../libs/logger";
// CONTEXT
import { useCreativeState, useCreativeDispatch } from "../../../../contexts/creative.context";
import { useUserState } from "../../../../contexts/user.context";
import { useNotificationDispatch } from "../../../../contexts/notification.context";
import { useMediaLoadingDispatch } from "../../../../contexts/mediaLoading.context";
// HELPERS
import get_file_width_height from "../../../../helpers/get_file_width_height";
// TYPES
import { SocialExtensionResult, Frame } from "../types";
import { setCreativeTTB } from "../../../../helpers/time_to_build";

export function SocialExtension() {
  const creative = useCreativeState();
  const creativeDispatch = useCreativeDispatch();
  const user = useUserState();
  const notificationDispatch = useNotificationDispatch();
  const mediaLoadingDispatch = useMediaLoadingDispatch();

  async function handleSocialFetch() {
    try {
      Google_Analytics_Event({ name: "social_fetch_start", category: "ad_creative", action: "fetch", label: creative.metadata.socialUrl });
      const startTime = performance.now();

      /**
       * HTL (02-06-23)
       * Adding in event tracking for total scrapes, regardless of whether they are successful or not.
       */
      if (config.analytics.DISABLED === false && user.teamId !== "pi6j") {
        analytics_record({
          name: "Ads_Library_tool",
          attributes: {
            eventType: "click",
            userId: `${user.id}`,
            userEmail: `${user.email}`,
            teamId: `${user.teamId}`,
            teamName: `${user.teamName}`,
          },
        });
      }
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Fetching brand info and assets.\n Please wait, this process can take up to 1 minute.", progress: Math.floor(Math.random() * (19 - 11 + 1) + 11) } });
      /**
       * FETCH DATA
       */
      const pageData: SocialExtensionResult = await fetchAdsLibraryPageData(creative.metadata.socialUrl);
      /**
       * AF (30-08-23)
       * Create an object with properties required for a successful social fetch according to format
       */
      let successfulScrapeObject = {
        brandName: pageData.brand.username,
        brandLogoUrl: pageData.brand.profilePictureUrl,
        copy: pageData.brand.postDescription,
        cta: pageData.cta.text,
        clickOutUrl: pageData.cta.link,
        frameAssetUrl: pageData.frames.map((frame) => frame.url),
        frameText: pageData.frames.map((frame) => (pageData.media.type === "carousel" ? frame.title : "n/a for non-carousel formats")),
      };
      /**
       * AF (30-08-23)
       * Use the some method to check if any of the required properties are empty strings
       * and therefore only a partial success scrape
       */
      const pageDataHasMissingValues = Object.values(successfulScrapeObject)
        .flat()
        .some((x: string) => x === "");

      /**
       * HTL (09-10-23)
       * Time to build stats
       */
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      setCreativeTTB("socialFetch", responseTime.toString());
      /**
       * HTL (14-06-23)
       * Find the best fitting format and aspect ratio based on the first frame.
       * Set format and aspect ratio
       * AF (16-08-23)
       * for video ads we use the video preview image to access dimensions as the preview url of the video itself isn't accessible
       */
      let firstFrameProxyFile = await fetchMediaAssetWithProxy(pageData.media.poster ? pageData.media.poster : pageData.frames[0].url);
      let dimensions = await get_file_width_height(firstFrameProxyFile.body, firstFrameProxyFile.mimeType);
      let mostApplicableCreative = await mostApplicableFormatAndAspectRatio(dimensions, pageData.media.type, creative.metadata.format, notificationDispatch);
      creativeDispatch({
        type: "UPDATE_CREATIVE_METADATA_ASPECTRATIO",
        payload: { aspectRatio: mostApplicableCreative.aspectRatio },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_METADATA_FORMAT",
        payload: { format: mostApplicableCreative.format },
      });
      /**
       * PROXY
       * Use proxy to return files from CDN and upload them to S3
       */
      const proxyData = await uploadMediaAssetsWithProxy({ data: pageData, aspectRatio: mostApplicableCreative.aspectRatio, format: mostApplicableCreative.format, teamId: user.teamId, creative, creativeDispatch, notificationDispatch, mediaLoadingDispatch });
      /**
       * CONTEXT
       * Set text data in context
       */
      creativeDispatch({
        type: "UPDATE_CREATIVE_METADATA_BRANDNAME",
        payload: { brandName: proxyData.brand.username },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_METADATA_CONTENT",
        payload: { content: proxyData.brand.postDescription },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_METADATA_BUTTON_CLICKOUT",
        payload: { clickout: proxyData.cta.link },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_METADATA_BUTTON_TEXT",
        payload: { text: proxyData.cta.text },
      });
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      /**
       * HTL (02-06-23)
       * Adding in event tracking for partial success and successful scrapes.
       */
      if (config.analytics.DISABLED === false && user.teamId !== "pi6j") {
        if (pageDataHasMissingValues) {
          analytics_record({
            name: "social_fetch_partial",
            attributes: {
              eventType: "click",
              userId: `${user.id}`,
              userEmail: `${user.email}`,
              teamId: `${user.teamId}`,
              teamName: `${user.teamName}`,
              score: `${JSON.stringify(successfulScrapeObject)}`,
              assetType: `aspect ratio of scraped asset: ${mostApplicableCreative.incomingAspectRatio}`,
            },
          });
        }
        if (!pageDataHasMissingValues) {
          analytics_record({
            name: "Success_Scrape",
            attributes: {
              eventType: "click",
              userId: `${user.id}`,
              userEmail: `${user.email}`,
              teamId: `${user.teamId}`,
              teamName: `${user.teamName}`,
              assetType: `aspect ratio of scraped asset: ${mostApplicableCreative.incomingAspectRatio}`,
            },
          });
        }
      }
      Google_Analytics_Event({ name: "social_fetch_success", category: "ad_creative", action: "fetch", label: creative.metadata.socialUrl });
    } catch (error) {
      Google_Analytics_Event({ name: "social_fetch_failure", category: "ad_creative", action: "fetch", label: creative.metadata.socialUrl });
      logger({ error, user, context: { origin: "SocialExtension_handleAutofill", input: creative.metadata.socialUrl } });
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      return notificationDispatch({
        type: "ADD",
        payload: {
          header: error.message,
          message: error.cause,
          type: "error",
          autoDismiss: true,
        },
      });
    }
  }

  return (
    <>
      {/* Title */}
      <div className="flex flex-row">
        <p className="ma0 fw5 text-size-5 color-text-4 platform-font">Meta Ads Library Integration</p>
        <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={2} />
        <img src={betaLogo} className="w2" />
      </div>
      <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
      {/* Horizontal rule */}
      <ComponentLibrary.PCDivider vertical={false} />
      <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={2} />
      {/* Subtitle text and link to instructions */}
      <div className="flex flex-row w-100 items-end">
        <label className="ma0 fw5 color-text-2 platform-font i" style={{ fontSize: 9, letterSpacing: "-0.1px" }}>
          Autofill content directly from a Meta Ads Library URL to populate the ad build.
        </label>
        <Link
          className="underline pink"
          to={{
            pathname: "https://www.notion.so/picnicmedia/Meta-Ads-Library-Integration-How-To-Guide-ca94a146225549d081bb5c8eda9541ea",
          }}
          target="_blank">
          <label className="ma0 ml1 fw5 platform-font pointer i" style={{ fontSize: 9, letterSpacing: "-0.1px", color: "#fc8597" }}>
            Click here for full instructions.
          </label>
        </Link>
      </div>
      <div className="flex flex-row items-end">
        {/* URL input */}
        <div className="w-100">
          <ComponentLibrary.PCSpacer type={"margin"} position={"right"} size={1}>
            <ComponentLibrary.PCInput
              subHeader={""}
              label={""}
              placeholder={""}
              value={creative.metadata.socialUrl}
              handleChange={(e: any) =>
                creativeDispatch({
                  type: "UPDATE_CREATIVE_METADATA_SOCIALURL",
                  payload: { socialUrl: e.target.value },
                })
              }
              type={"text"}
            />
          </ComponentLibrary.PCSpacer>
        </div>
        {/* Submit button */}
        <ComponentLibrary.PCSpacer type={"margin"} position={"bottom"} size={2}>
          <ComponentLibrary.PCActionButton
            text={"Go"}
            clickHandler={(e: any) => {
              handleSocialFetch();
            }}
            textColor={"white"}
            color={"background-color-primary-3"}
          />
        </ComponentLibrary.PCSpacer>
      </div>
    </>
  );
}
